<script setup lang="ts">
import type { PropType } from 'vue'
import type { Category, Tag } from '@/models/content'
import { useAuthStore } from '@/stores/auth'
import { useMembershipStore } from '@/stores/membership'
import { useOpenPDFViewer } from '~~/composables/open-pdf-viewer'
import {
  makeTwitterSharableLink,
  makeLinkedinSharableLink,
} from '@/utils/string'

const { openPDFViewer } = useOpenPDFViewer()
const membershipStore = useMembershipStore()

const authStore = useAuthStore()
const { callToAction, isDownloading, downloadProgress, cancelDownload } =
  useDownloadFile({ module: MODULES.SPECIAL_ISSUES })
const { $cloudinary } = useNuxtApp()

const props = defineProps({
  id: {
    type: String,
    require: true,
  },
  linkFlipHtml5: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    required: true,
  },
  formId: {
    type: String,
    required: true,
  },
  summary: {
    type: String,
    required: true,
  },
  pills: {
    type: Array as PropType<Tag[] | Category[]>,
    required: true,
  },
  coverUrl: {
    type: String,
    required: true,
  },
  fileName: {
    type: String,
    required: true,
  },
  keepMobileLayout: {
    type: Boolean,
    default: false,
  },
  publishDate: {
    type: String,
    required: true,
  },
  subscriptionRequired: {
    type: String,
    default: undefined,
  },
  specialIssueData: {
    type: Object,
    default: undefined,
  },
})
const $config = useRuntimeConfig()

const coverPosition = useCoverPosition(props.coverUrl)
const url = `${window.location.href}/${props.id}`
const twitterUrl = makeTwitterSharableLink(url, props.title)
const linkedinUrl = makeLinkedinSharableLink(url, props.title)

const isGigafactoriesForm = computed(
  () => props.formId === $config.public.formidableForms.specialIssues,
)
const isAuthorized = computed(
  () =>
    membershipStore.isSubscriberActive ||
    membershipStore.isSpecialIssuesSubActive,
)

const emit = defineEmits(['openModal'])

const openModal = () => {
  emit('openModal')
}

function download() {
  callToAction.value.callback(props.id, 'special-issues', props.fileName, props.specialIssueData || {})
}
</script>

<template>
  <article
    class="card-hover group !cursor-default bg-warm-gray px-4 pb-5 pt-10"
    data-testid="magazine-card"
  >
    <div class="flex items-center justify-center gap-4 md:gap-8">
      <div class="hidden h-full shrink-0 md:block md:w-[340px]">
        <img
          width="392"
          height="195"
          class="h-full w-full object-cover"
          :class="coverPosition"
          :src="$cloudinary.resize(coverUrl, 'special-issues-card')"
          alt=""
        />
      </div>
      <div
        class="flex grow flex-col gap-2"
        :class="{ 'md:flex-row md:gap-10': keepMobileLayout }"
      >
        <div>
          <div class="flex flex-col justify-between md:flex-row md:gap-x-3">
            <p
              class="text-xl font-bold md:text-base md:font-semibold"
              data-testid="magazine-card-title"
            >
              {{ title }}
            </p>
            <p class="mb-3 text-sm font-semibold md:hidden">
              {{ publishDate }}
            </p>
            <div class="flex h-full w-full shrink-0 justify-center md:hidden">
              <img
                width="274"
                height="136"
                class="h-full w-[274px] object-cover object-center"
                :class="coverPosition"
                :src="$cloudinary.resize(coverUrl, 'special-issues-card')"
                alt=""
              />
            </div>
            <div class="hidden h-fit items-center justify-start gap-1 md:flex">
              <p class="text-xs">Share</p>
              <TooltipShare
                placement="top"
                :twitter-link="twitterUrl"
                :linkedin-link="linkedinUrl"
                :link="url"
                class="mr-3"
              />
            </div>
          </div>
          <p class="hidden text-sm md:block">{{ publishDate }}</p>
          <p
            class="mt-3 text-sm md:mt-2"
            data-testid="magazine-card-summary"
            v-html="summary"
          ></p>
          <div
            class="mt-7 flex items-center justify-between"
            :class="[pills.length ? 'md:justify-between' : 'md:justify-end']"
          >
            <div v-if="pills.length" class="hidden flex-wrap gap-2 md:flex">
              <LinkedPill
                v-for="pill in pills"
                :key="pill.id"
                :slug="pill.slug"
                variant="black"
              >
                {{ pill.name }}
              </LinkedPill>
            </div>
            <div class="flex items-center justify-start gap-1 md:hidden">
              <p class="text-xs">Share</p>
              <TooltipShare
                placement="top"
                :twitter-link="twitterUrl"
                :linkedin-link="linkedinUrl"
                :link="url"
                class="mr-3"
              />
            </div>
            <div class="flex h-10 items-center justify-center">
              <div class="flex w-full items-center justify-center gap-2">
                <BaseButton
                  v-if="linkFlipHtml5 && isAuthorized"
                  variant="primary"
                  class="w-full max-w-[140px] shrink-0 grow md:max-w-[110px] md:grow-0"
                  data-testid="view-pdf"
                  @click="
                    openPDFViewer(id, title, linkFlipHtml5, 'specialIssue', subscriptionRequired || undefined)
                  "
                >
                  Read Now
                </BaseButton>
                <div
                  v-if="authStore.isAuthenticated && !isGigafactoriesForm"
                  class="shrink-0"
                >
                  <CircularProgressBar
                    v-if="isDownloading || downloadProgress"
                    :progress="downloadProgress"
                    :cancel-call-back="cancelDownload"
                    :is-loading="isDownloading"
                    @click="download"
                  />
                  <BaseButton
                    v-else
                    variant="tertiary"
                    class="grow bg-white md:grow-0"
                    data-testid="call-to-action"
                    @click="download"
                  >
                    {{
                      callToAction.text === 'Subscribe'
                        ? 'Download Report'
                        : callToAction.text
                    }}
                  </BaseButton>
                </div>
                <BaseButton
                  v-else
                  variant="tertiary"
                  class="z-10 shrink-0 grow bg-white md:grow-0"
                  data-testid="call-to-action"
                  @click="openModal"
                >
                  Download Report
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
